import { useMemo, useState } from "react";
import {
  ExternalLink,
  FileSearch,
  Plus,
  RefreshCw,
  ShieldAlert,
  X,
} from "lucide-react";
import { CDM_POLICY_RULES } from "../data/cdmPolicyPack";
import { RELATED_POLICY_RULES } from "../data/regulatoryGatewayPack";
import { assessProject } from "../lib/assessmentEngine";
import { blankRegulatoryChange } from "../lib/projectStore";
import type {
  DesignRisk,
  DutyAction,
  Project,
  RegulatoryChangeRecord,
} from "../types/domain";

interface RegulatoryChangesViewProps {
  project: Project;
  changes: RegulatoryChangeRecord[];
  dutyActions: DutyAction[];
  risks: DesignRisk[];
  onSave: (change: RegulatoryChangeRecord) => void;
  onRemove: (id: string) => void;
}

const rules = [...CDM_POLICY_RULES, ...RELATED_POLICY_RULES];

export function RegulatoryChangesView({
  project,
  changes,
  dutyActions,
  risks,
  onSave,
  onRemove,
}: RegulatoryChangesViewProps) {
  const [draft, setDraft] = useState<RegulatoryChangeRecord | null>(null);
  const applicableIds = useMemo(
    () => new Set(assessProject(project).triggeredRules.map((rule) => rule.id)),
    [project],
  );

  const assessImpact = (change: RegulatoryChangeRecord) => {
    const impactedDutyIds = change.affectedRuleIds.filter((id) => applicableIds.has(id));
    const impactedRiskIds = risks
      .filter((risk) =>
        risk.regulationIds.some((id) => change.affectedRuleIds.includes(id)),
      )
      .map((risk) => risk.id);
    setDraft({
      ...change,
      impactedDutyIds,
      impactedRiskIds,
      impactStatus:
        impactedDutyIds.length || impactedRiskIds.length ? "action-required" : "no-impact",
      assessedAt: new Date().toISOString(),
    });
  };

  return (
    <>
      <section className="module-intro">
        <div>
          <span className="feature-kicker">
            <FileSearch size={17} /> Regulatory change impact
          </span>
          <h2>Know which live duties and design decisions a rule change affects.</h2>
          <p>
            Record the authoritative change notice, map affected clauses to the controlled
            policy graph, identify active project duties and reopen linked decisions for a
            named owner.
          </p>
        </div>
        <button
          className="button button-primary"
          onClick={() => setDraft(blankRegulatoryChange())}
        >
          <Plus size={17} /> Record change
        </button>
      </section>

      {changes.length === 0 ? (
        <section className="empty-state">
          <ShieldAlert size={32} />
          <h3>No regulatory change records</h3>
          <p>
            Add a Gazette, regulator circular or controlled policy-pack release. The
            impact engine will compare it with this project’s live duty and risk graph.
          </p>
        </section>
      ) : (
        <section className="change-register">
          {changes.map((change) => (
            <article key={change.id}>
              <header>
                <div>
                  <span className="eyebrow">
                    {change.previousPackVersion || "Unversioned"} →{" "}
                    {change.newPackVersion || "Unversioned"}
                  </span>
                  <h3>{change.instrument || "Untitled regulatory change"}</h3>
                </div>
                <em className={`change-status status-${change.impactStatus}`}>
                  {change.impactStatus}
                </em>
              </header>
              <p>{change.summary || "No change summary recorded."}</p>
              <div className="change-impact-grid">
                <span>
                  <strong>{change.affectedRuleIds.length}</strong> affected clauses
                </span>
                <span>
                  <strong>{change.impactedDutyIds.length}</strong> live project duties
                </span>
                <span>
                  <strong>{change.impactedRiskIds.length}</strong> linked design risks
                </span>
                <span>
                  <strong>
                    {
                      dutyActions.filter(
                        (action) =>
                          change.impactedDutyIds.includes(action.ruleId) &&
                          action.status !== "complete",
                      ).length
                    }
                  </strong>{" "}
                  open responses
                </span>
              </div>
              <footer>
                {change.sourceUrl && (
                  <a href={change.sourceUrl} target="_blank" rel="noreferrer">
                    Open authoritative notice <ExternalLink size={13} />
                  </a>
                )}
                <button className="button-link" onClick={() => setDraft(change)}>
                  Review impact
                </button>
                <button className="button-link danger-text" onClick={() => onRemove(change.id)}>
                  Remove
                </button>
              </footer>
            </article>
          ))}
        </section>
      )}

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card wide-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Controlled regulatory update</span>
                <h2>Assess project impact</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <label className="field wide">
                  <span>Instrument or notice</span>
                  <input
                    value={draft.instrument}
                    onChange={(event) => setDraft({ ...draft, instrument: event.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Previous pack version</span>
                  <input
                    value={draft.previousPackVersion}
                    onChange={(event) =>
                      setDraft({ ...draft, previousPackVersion: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>New pack version</span>
                  <input
                    value={draft.newPackVersion}
                    onChange={(event) =>
                      setDraft({ ...draft, newPackVersion: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Published date</span>
                  <input
                    type="date"
                    value={draft.publishedDate}
                    onChange={(event) =>
                      setDraft({ ...draft, publishedDate: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Effective date</span>
                  <input
                    type="date"
                    value={draft.effectiveDate}
                    onChange={(event) =>
                      setDraft({ ...draft, effectiveDate: event.target.value })
                    }
                  />
                </label>
                <label className="field wide">
                  <span>Authoritative source URL</span>
                  <input
                    type="url"
                    value={draft.sourceUrl}
                    onChange={(event) => setDraft({ ...draft, sourceUrl: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Change summary</span>
                  <textarea
                    rows={4}
                    value={draft.summary}
                    onChange={(event) => setDraft({ ...draft, summary: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Affected controlled clauses</span>
                  <div className="check-grid rule-check-grid">
                    {rules.map((rule) => (
                      <label key={rule.id}>
                        <input
                          type="checkbox"
                          checked={draft.affectedRuleIds.includes(rule.id)}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              affectedRuleIds: event.target.checked
                                ? [...draft.affectedRuleIds, rule.id]
                                : draft.affectedRuleIds.filter((id) => id !== rule.id),
                            })
                          }
                        />
                        <span>
                          <strong>{rule.regulation}</strong> {rule.title}
                        </span>
                      </label>
                    ))}
                  </div>
                </label>
                <label className="field">
                  <span>Impact owner</span>
                  <input
                    value={draft.owner}
                    onChange={(event) => setDraft({ ...draft, owner: event.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Due date</span>
                  <input
                    type="date"
                    value={draft.dueDate}
                    onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })}
                  />
                </label>
                <label className="field wide">
                  <span>Impact decision and required action</span>
                  <textarea
                    rows={3}
                    value={draft.decision}
                    onChange={(event) => setDraft({ ...draft, decision: event.target.value })}
                  />
                </label>
              </div>
              <div className="impact-preview">
                <button
                  className="button button-secondary"
                  onClick={() => assessImpact(draft)}
                >
                  <RefreshCw size={16} /> Recalculate impact
                </button>
                <span>
                  {draft.impactedDutyIds.length} live duties · {draft.impactedRiskIds.length} risks
                </span>
              </div>
            </div>
            <div className="modal-actions">
              <button className="button button-secondary" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                className="button button-primary"
                disabled={!draft.instrument || !draft.sourceUrl || !draft.owner}
                onClick={() => {
                  const next = {
                    ...draft,
                    assessedAt: draft.assessedAt || new Date().toISOString(),
                  };
                  onSave(next);
                  setDraft(null);
                }}
              >
                Save impact assessment
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
